import type { FormField } from '@irace/shared';

/**
 * §8 — input attributes per configured field type. The keyboard a participant
 * gets is decided here: `inputmode` and `autocomplete` are the difference
 * between a two-tap phone entry and a fight with a QWERTY keyboard in the sun.
 */
const TYPE_ATTRIBUTES: Record<
  FormField['type'],
  Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'inputMode' | 'autoCapitalize' | 'spellCheck' | 'enterKeyHint'
  >
> = {
  text: { type: 'text', enterKeyHint: 'next' },
  tel: { type: 'tel', inputMode: 'tel', enterKeyHint: 'next' },
  email: {
    type: 'email',
    inputMode: 'email',
    autoCapitalize: 'none',
    spellCheck: false,
    enterKeyHint: 'done',
  },
};

const PLACEHOLDERS: Record<string, string> = {
  full_name: 'Nguyễn Văn A',
  phone: '09xx xxx xxx',
  email: 'ban@email.com',
};

export function FormFieldInput({
  field,
  value,
  error,
  inputRef,
  onChange,
  onBlur,
}: {
  field: FormField;
  value: string;
  error: string | null;
  inputRef: (element: HTMLInputElement | null) => void;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  const inputId = `field-${field.key}`;
  const errorId = `${inputId}-error`;
  const helpId = `${inputId}-help`;
  const describedBy = [error ? errorId : null, field.help_text ? helpId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-semibold">
        {field.label}
        {field.required ? (
          <span className="text-danger" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </label>
      <input
        {...TYPE_ATTRIBUTES[field.type]}
        id={inputId}
        ref={inputRef}
        name={field.key}
        value={value}
        required={field.required}
        maxLength={field.max_length}
        autoComplete={field.autocomplete}
        placeholder={PLACEHOLDERS[field.key]}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onBlur={onBlur}
        className={`h-13 rounded-control border-[1.5px] bg-surface px-4 text-base text-text outline-none ${
          error ? 'border-danger' : 'border-border'
        }`}
      />
      {field.help_text ? (
        <p id={helpId} className="text-xs text-text-muted">
          {field.help_text}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-xs font-semibold text-danger">
          <span aria-hidden="true">⚠ </span>
          {error}
        </p>
      ) : null}
    </div>
  );
}
